import { Box, Popover, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import CustomInput from './CustomInput'

export const MIN_DURATION_SECONDS = 15
export const MAX_DURATION_SECONDS = 1800
export const DURATION_PLACEHOLDER = 'What is the duration of your video?'
// const STEP_SECONDS = 5 // used by the +/- step buttons, disabled for now
const DARK_GREEN = '#08231B'

export const hasDurationValue = (value) => value !== '' && value !== null && value !== undefined

export const clampDuration = (value) => Math.min(MAX_DURATION_SECONDS, Math.max(MIN_DURATION_SECONDS, Math.round(Number(value) || 0)))

export const formatDurationLabel = (totalSeconds) => {
    const safe = Math.max(0, Math.round(Number(totalSeconds) || 0))
    const minutes = Math.floor(safe / 60)
    const seconds = safe % 60
    const minutesLabel = minutes > 0 ? `${minutes} minute${minutes !== 1 ? 's' : ''}` : ''
    const secondsLabel = seconds > 0 ? `${seconds} second${seconds !== 1 ? 's' : ''}` : ''
    if (minutesLabel && secondsLabel) return `${minutesLabel} ${secondsLabel}`
    return minutesLabel || secondsLabel || '0 seconds'
}

const MAX_MINUTES = Math.floor(MAX_DURATION_SECONDS / 60)
const MINUTE_OPTIONS = Array.from({ length: MAX_MINUTES + 1 }, (_, i) => i)
const SECOND_OPTIONS = Array.from({ length: 60 }, (_, i) => i)

const ScrollWheel = ({ options, value, onChange, label, medium }) => {
    const containerRef = useRef(null)
    const scrollTimeout = useRef(null)
    const isProgrammaticScroll = useRef(false)

    const itemHeight = medium ? 33 : 26
    const visibleItems = 5
    const wheelHeight = itemHeight * visibleItems
    const wheelPadding = (wheelHeight - itemHeight) / 2

    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        isProgrammaticScroll.current = true
        const index = Math.max(0, options.indexOf(value))
        el.scrollTop = index * itemHeight
        const resetFlag = setTimeout(() => { isProgrammaticScroll.current = false }, 50)
        return () => clearTimeout(resetFlag)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // options can shift (e.g. seconds' floor changes when minutes hits 0) — keep the scroll position in sync
    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const index = Math.max(0, options.indexOf(value))
        const targetTop = index * itemHeight
        if (Math.abs(el.scrollTop - targetTop) > 1) {
            isProgrammaticScroll.current = true
            el.scrollTop = targetTop
            const resetFlag = setTimeout(() => { isProgrammaticScroll.current = false }, 50)
            return () => clearTimeout(resetFlag)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options])

    const commitFromScroll = () => {
        const el = containerRef.current
        if (!el) return
        const index = Math.round(el.scrollTop / itemHeight)
        const clampedIndex = Math.min(options.length - 1, Math.max(0, index))
        if (options[clampedIndex] !== value) onChange(options[clampedIndex])
    }

    const handleScroll = () => {
        if (isProgrammaticScroll.current) return
        clearTimeout(scrollTimeout.current)
        scrollTimeout.current = setTimeout(commitFromScroll, 120)
    }

    const handleItemClick = (optionValue) => {
        const el = containerRef.current
        if (!el) return
        const index = Math.max(0, options.indexOf(optionValue))
        el.scrollTo({ top: index * itemHeight, behavior: 'smooth' })
        onChange(optionValue)
    }

    return (
        <Box sx={{ position: 'relative', width: medium ? '76px' : '58px' }}>
            <Typography sx={{ fontFamily: 'Rubik', fontSize: medium ? '10px' : '8px', fontWeight: 600, mb: 0.5, textAlign: 'center', color: 'rgba(0,0,0,0.6)' }}>{label}</Typography>
            <Box sx={{ position: 'relative', height: `${wheelHeight}px` }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: `${wheelPadding}px`,
                        left: 0,
                        right: 0,
                        height: `${itemHeight}px`,
                        borderTop: '1.5px solid #000',
                        borderBottom: '1.5px solid #000',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
                <Box
                    ref={containerRef}
                    onScroll={handleScroll}
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        height: '100%',
                        overflowY: 'scroll',
                        scrollSnapType: 'y mandatory',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                        py: `${wheelPadding}px`,
                    }}
                >
                    {options.map((option) => (
                        <Box
                            key={option}
                            onClick={() => handleItemClick(option)}
                            sx={{
                                height: `${itemHeight}px`,
                                scrollSnapAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Rubik',
                                    fontSize: option === value ? (medium ? '16px' : '13px') : (medium ? '13px' : '11px'),
                                    fontWeight: option === value ? 700 : 400,
                                    color: option === value ? DARK_GREEN : 'rgba(0,0,0,0.4)',
                                }}
                            >
                                {option.toString().padStart(2, '0')}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

const DurationPicker = (props) => {
    const { value, onChange, medium, errorFlag, errorMsg, msgColor } = props
    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)

    const seconds = clampDuration(value)
    const minutesPart = Math.floor(seconds / 60)
    const secondsPart = seconds % 60

    const handleOpen = (event) => setAnchorEl(event.currentTarget)
    const handleClose = () => setAnchorEl(null)

    // const handleStep = (delta) => {
    //     onChange(clampDuration(seconds + delta))
    // }

    const handleMinutesChange = (newMinutes) => {
        // total duration can't drop below MIN_DURATION_SECONDS, so 0 minutes forces at least that many seconds
        const adjustedSeconds = newMinutes === 0 ? Math.max(secondsPart, MIN_DURATION_SECONDS) : secondsPart
        onChange(clampDuration(newMinutes * 60 + adjustedSeconds))
    }

    const handleSecondsChange = (newSeconds) => {
        onChange(clampDuration(minutesPart * 60 + newSeconds))
    }

    // when 0 minutes are selected, seconds below MIN_DURATION_SECONDS would drop under the overall minimum
    const secondOptionsForMinutes = minutesPart === 0 ? SECOND_OPTIONS.filter((s) => s >= MIN_DURATION_SECONDS) : SECOND_OPTIONS

    return (
        <Box sx={{ width: '100%' }}>
        <Box className='flex a-center' gap={{ xs: 1, xl: 1.5 }} sx={{ width: '100%' }}>
            <Box onClick={handleOpen} sx={{ cursor: 'pointer', flexGrow: 1, minWidth: 0 }}>
                <CustomInput
                    medium={medium}
                    fullWidth
                    errorFlag={errorFlag}
                    placeholder={DURATION_PLACEHOLDER}
                    value={hasDurationValue(value) ? formatDurationLabel(seconds) : ''}
                    onChange={() => {}}
                    onKeyDown={(event) => event.preventDefault()}
                />
            </Box>

            {/* Step +/- buttons — disabled for now, may bring back later.
            <IconButton
                onClick={() => handleStep(-STEP_SECONDS)}
                disabled={seconds <= MIN_DURATION_SECONDS}
                sx={{
                    backgroundColor: DARK_GREEN,
                    borderRadius: '5px',
                    width: { xs: '26px', md: '30px', xl: '44px' },
                    height: { xs: '26px', md: '30px', xl: '44px' },
                    flexShrink: 0,
                    '&:hover': { backgroundColor: DARK_GREEN, opacity: 0.85 },
                    '&.Mui-disabled': { backgroundColor: DARK_GREEN, opacity: 0.4 },
                }}
            >
                <Typography sx={{ fontSize: { xs: '18px', md: '20px', xl: '28px' }, fontWeight: 900, color: '#fff', lineHeight: 1 }}>−</Typography>
            </IconButton>

            <IconButton
                onClick={() => handleStep(STEP_SECONDS)}
                disabled={seconds >= MAX_DURATION_SECONDS}
                sx={{
                    backgroundColor: DARK_GREEN,
                    borderRadius: '5px',
                    width: { xs: '26px', md: '30px', xl: '44px' },
                    height: { xs: '26px', md: '30px', xl: '44px' },
                    flexShrink: 0,
                    '&:hover': { backgroundColor: DARK_GREEN, opacity: 0.85 },
                    '&.Mui-disabled': { backgroundColor: DARK_GREEN, opacity: 0.4 },
                }}
            >
                <Typography sx={{ fontSize: { xs: '18px', md: '20px', xl: '28px' }, fontWeight: 900, color: '#fff', lineHeight: 1 }}>+</Typography>
            </IconButton>
            */}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box className='flex a-center j-center' gap={medium ? 2 : 1.2} p={medium ? 2.5 : 1.5}>
                    <ScrollWheel options={MINUTE_OPTIONS} value={minutesPart} onChange={handleMinutesChange} label='Min' medium={medium} />
                    <Typography sx={{ fontSize: medium ? '18px' : '14px', fontWeight: 600, color: DARK_GREEN, mt: medium ? '18px' : '14px' }}>:</Typography>
                    <ScrollWheel options={secondOptionsForMinutes} value={secondsPart} onChange={handleSecondsChange} label='Sec' medium={medium} />
                </Box>
            </Popover>
        </Box>
        {errorFlag && (
            <Typography
                textAlign={'right'}
                variant="body1"
                className="errorDom"
                component="span"
                sx={{ fontFamily: 'Rubik', color: msgColor ? msgColor : "rgb(211, 47, 47)", fontSize: medium ? {xs:'12px',lg:'13px',xl:'18px'} : {xs:'7px',md:'8px',lg:'9px',xl:'14px'}, fontWeight: msgColor && 600 }}
            >
                <Box>{errorMsg}</Box>
            </Typography>
        )}
        </Box>
    )
}

export default DurationPicker
